import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Form from '@rjsf/mui';
import type { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import {
  ASK_USER_CHOICE_TOOL_NAME,
  OTHER_OPTION_VALUE,
  PRESENT_PLAN_TOOL_NAME,
  createDraftForInterrupt,
  getPlanDraft,
  parseAskUserChoiceArgs,
  parsePresentPlanArgs,
  type AskUserChoiceArgs,
  type AskUserChoiceDraft,
  type HitlAnswerDraft,
  type PendingHitlInterrupt,
  type PlanApprovalDraft,
} from './types';
import { PlanApprovalCard } from './PlanApprovalCard';

interface HitlInterruptPanelProps {
  interrupts: PendingHitlInterrupt[];
  drafts: Record<string, HitlAnswerDraft | undefined>;
  onDraftChange: (interruptId: string, draft: HitlAnswerDraft) => void;
  /**
   * Submit current drafts. Optional `overrides` lets callers pass an
   * authoritative draft for a specific interruptId — used by PlanApprovalCard
   * to avoid the React state-update race where a freshly-set draft hasn't
   * propagated by the time onSubmit's closure runs.
   */
  onSubmit: (
    overrides?: Record<string, HitlAnswerDraft>
  ) => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isSubmitting: boolean;
  isCancelling?: boolean;
}

interface AskUserChoiceCardProps {
  interrupt: PendingHitlInterrupt;
  args: AskUserChoiceArgs;
  draft: AskUserChoiceDraft;
  onChange: (next: AskUserChoiceDraft) => void;
  onAutoAdvance?: () => void;
}

const getAskDraft = (
  interrupt: PendingHitlInterrupt,
  draft: HitlAnswerDraft | undefined
): AskUserChoiceDraft => {
  if (draft?.mode === 'ask_user_choice') {
    return draft.value;
  }

  const fallback = createDraftForInterrupt(interrupt);
  if (fallback.mode === 'ask_user_choice') {
    return fallback.value;
  }

  return {
    selectedValues: [],
    otherText: '',
    skipped: false,
  };
};

const AskUserChoiceCard: React.FC<AskUserChoiceCardProps> = ({
  interrupt,
  args,
  draft,
  onChange,
  onAutoAdvance,
}) => {
  const isMultiSelect = Boolean(args.multiSelect);
  const otherLabel = args.otherLabel || 'Other';
  const allowsOther = args.allowOther ?? true;
  const allowsSkip = Boolean(args.allowSkip);

  const isSelected = (value: string) => draft.selectedValues.includes(value);

  const toggleSingleOption = (value: string) => {
    onChange({
      selectedValues: [value],
      otherText: value === OTHER_OPTION_VALUE ? draft.otherText : '',
      skipped: false,
    });
    
    if (value !== OTHER_OPTION_VALUE && onAutoAdvance) {
      setTimeout(() => onAutoAdvance(), 150);
    }
  };

  const toggleMultiOption = (value: string) => {
    const currentlySelected = new Set(draft.selectedValues);
    if (currentlySelected.has(value)) {
      currentlySelected.delete(value);
    } else {
      currentlySelected.add(value);
    }

    onChange({
      selectedValues: Array.from(currentlySelected),
      otherText: currentlySelected.has(OTHER_OPTION_VALUE) ? draft.otherText : '',
      skipped: false,
    });
  };

  const toggleSkip = () => {
    if (draft.skipped) {
      onChange({
        ...draft,
        skipped: false,
      });
      return;
    }

    onChange({
      selectedValues: [],
      otherText: '',
      skipped: true,
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="body2" fontWeight={500} sx={{ opacity: 0.8 }}>
          {args.question}
        </Typography>

        <Stack spacing={1} sx={{ mt: 1 }}>
          {args.options.map((option: any, index: number) => {
            const checked = isSelected(option.value);
            return (
              <Box
                key={`${interrupt.interruptId}-${option.value}`}
                onClick={() => {
                  if (draft.skipped) return;
                  if (isMultiSelect) {
                    toggleMultiOption(option.value);
                    return;
                  }
                  toggleSingleOption(option.value);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: checked ? 'action.selected' : 'transparent',
                  cursor: draft.skipped ? 'default' : 'pointer',
                  opacity: draft.skipped ? 0.6 : 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  transition: 'background-color 0.2s ease',
                  border: '1px solid transparent',
                  '&:hover': draft.skipped
                    ? undefined
                    : {
                        bgcolor: 'action.hover',
                      },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                      {option.label}
                    </Typography>
                  </Box>
                  {!isMultiSelect && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.5 }}>
                      →
                    </Typography>
                  )}
                  {isMultiSelect && (
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => {
                        if (draft.skipped) return;
                        toggleMultiOption(option.value);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      sx={{ p: 0 }}
                    />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {allowsOther && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isSelected(OTHER_OPTION_VALUE)}
                  disabled={draft.skipped}
                  onChange={() => {
                    if (draft.skipped) return;
                    if (isMultiSelect) {
                      toggleMultiOption(OTHER_OPTION_VALUE);
                      return;
                    }
                    toggleSingleOption(OTHER_OPTION_VALUE);
                  }}
                />
              }
              label={otherLabel}
            />
            {isSelected(OTHER_OPTION_VALUE) && (
              <TextField
                fullWidth
                size="small"
                label={otherLabel}
                value={draft.otherText}
                onChange={(event) =>
                  onChange({
                    ...draft,
                    otherText: event.target.value,
                    skipped: false,
                  })
                }
                disabled={draft.skipped}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        )}

        {allowsSkip && (
          <Box>
            <Button size="small" onClick={toggleSkip} color={draft.skipped ? 'primary' : 'inherit'}>
              {draft.skipped ? 'Answer this question instead' : 'Skip this question'}
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

const GenericSchemaCard: React.FC<{
  interrupt: PendingHitlInterrupt;
  draft: HitlAnswerDraft | undefined;
  onDraftChange: (next: HitlAnswerDraft) => void;
}> = ({ interrupt, draft, onDraftChange }) => {
  const schema = (interrupt.schema && Object.keys(interrupt.schema).length > 0
    ? interrupt.schema
    : {
        type: 'object',
        properties: {
          response: {
            type: 'string',
            title: 'Response',
          },
        },
      }) as RJSFSchema;

  const formData = draft?.mode === 'generic' ? draft.value : {};

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: 3,
        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          {interrupt.toolName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          No custom UI is registered for this tool yet. Showing schema-driven fallback.
        </Typography>

        <Form
          schema={schema}
          formData={formData}
          validator={validator}
          noHtml5Validate
          liveValidate={false}
          showErrorList={false}
          onChange={(event) => {
            onDraftChange({
              mode: 'generic',
              value: event.formData,
            });
          }}
        >
          <Box />
        </Form>
      </Stack>
    </Paper>
  );
};

export const HitlInterruptPanel: React.FC<HitlInterruptPanelProps> = ({
  interrupts,
  drafts,
  onDraftChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isCancelling = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (interrupts.length === 0) {
    return null;
  }

  const currentIndex = Math.min(activeIndex, interrupts.length - 1);
  const interrupt = interrupts[currentIndex];

  const submitLabel = interrupts.find((i: any) => {
    const args = parseAskUserChoiceArgs(i.toolArgs);
    return typeof args?.submitLabel === 'string';
  });

  const resolvedSubmitLabel = submitLabel
    ? parseAskUserChoiceArgs(submitLabel.toolArgs)?.submitLabel
    : 'Submit';

  const isLast = currentIndex === interrupts.length - 1;
  const planArgs =
    interrupt.toolName === PRESENT_PLAN_TOOL_NAME
      ? parsePresentPlanArgs(interrupt.toolArgs)
      : null;
  const isPlanInterrupt = planArgs !== null;

  return (
    <Box sx={{ mb: 2 }}>
      {interrupts.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
          {currentIndex + 1} / {interrupts.length} questions
        </Typography>
      )}

      {isPlanInterrupt && planArgs ? (
        // NOTE: present_plan is currently always emitted as a standalone
        // interrupt by the planner subagent. We still pass
        // canSubmitPlan={isLast} so that if a future flow ever batches a
        // present_plan with later interrupts, the card's submit
        // affordances are disabled until the user navigates to the last
        // interrupt — preventing resume with an incomplete HITL payload.
        <PlanApprovalCard
          key={interrupt.interruptId}
          interrupt={interrupt}
          args={planArgs}
          draft={getPlanDraft(interrupt, drafts[interrupt.interruptId])}
          onChange={(next: PlanApprovalDraft) =>
            onDraftChange(interrupt.interruptId, {
              mode: 'present_plan',
              value: next,
            })
          }
          onSubmitDecision={(next: PlanApprovalDraft) => {
            // Pass an authoritative override so the parent submit closure
            // doesn't have to wait for React's setState propagation.
            const override: Record<string, HitlAnswerDraft> = {
              [interrupt.interruptId]: { mode: 'present_plan', value: next },
            };
            return onSubmit(override);
          }}
          isSubmitting={isSubmitting}
          canSubmitPlan={isLast}
        />
      ) : interrupt.toolName === ASK_USER_CHOICE_TOOL_NAME && parseAskUserChoiceArgs(interrupt.toolArgs) ? (
        <AskUserChoiceCard
          key={interrupt.interruptId}
          interrupt={interrupt}
          args={parseAskUserChoiceArgs(interrupt.toolArgs)!}
          draft={getAskDraft(interrupt, drafts[interrupt.interruptId])}
          onChange={(next) =>
            onDraftChange(interrupt.interruptId, {
              mode: 'ask_user_choice',
              value: next,
            })
          }
          onAutoAdvance={() => {
            if (!isLast) setActiveIndex((i: number) => i + 1);
          }}
        />
      ) : (
        <GenericSchemaCard
          key={interrupt.interruptId}
          interrupt={interrupt}
          draft={drafts[interrupt.interruptId]}
          onDraftChange={(next) => onDraftChange(interrupt.interruptId, next)}
        />
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        {currentIndex > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setActiveIndex((i: number) => i - 1)}
            disabled={isSubmitting || isCancelling}
          >
            Back
          </Button>
        )}

        {currentIndex === 0 && onCancel && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => { void onCancel(); }}
            disabled={isSubmitting || isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel'}
          </Button>
        )}

        {!isLast ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => setActiveIndex((i: number) => i + 1)}
          >
            Next
          </Button>
        ) : isPlanInterrupt ? (
          // PlanApprovalCard owns its own Approve/Request-Changes submit
          // affordances; suppress the panel-level Submit button to avoid
          // double-submit confusion.
          null
        ) : (
          <Button
            size="small"
            variant="contained"
            onClick={() => { void onSubmit(); }}
            disabled={isSubmitting || isCancelling}
            startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Submitting...' : resolvedSubmitLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default HitlInterruptPanel;
